import { Router } from "express";
import { db } from "@workspace/db";
import { proposalDraftsTable, proposalVersionsTable, tendersTable, analysesTable, companiesTable, tenderDocumentsTable } from "@workspace/db";
import { eq, and, desc, sql } from "drizzle-orm";
import { requireAuth, type AuthRequest } from "../lib/auth";
import { generateProposal, summarizeTenderDocument, generateProposalSection, inferMarketPricing, generateAllFinancialSections, PROPOSAL_SECTION_KEYS } from "../lib/openai";
import { evaluateReadiness } from "../lib/proposalReadiness.js";
import { broadcastTenderUpdate } from "../lib/socket";
import { logAudit } from "../lib/activity";
import multer from "multer";
import path from "path";
import fs from "fs";
import { fileURLToPath } from "url";
import { generateWordDocument, generatePdfDocument, generateWordFromTemplate, buildTemplateData } from "../lib/export.js";
import { generateProposalImage } from "../lib/openai.js";

const __filename = fileURLToPath(import.meta.url);
const __dirname = path.dirname(__filename);

const UPLOADS_DIR = path.join(__dirname, "../../uploads");
const LETTERHEADS_DIR = path.join(UPLOADS_DIR, "letterheads");
const TEMPLATES_DIR = path.join(UPLOADS_DIR, "templates");
const IMAGES_DIR = path.join(UPLOADS_DIR, "proposal-images");
fs.mkdirSync(LETTERHEADS_DIR, { recursive: true });
fs.mkdirSync(TEMPLATES_DIR, { recursive: true });
fs.mkdirSync(IMAGES_DIR, { recursive: true });

const letterheadStorage = multer.diskStorage({
  destination: (_req, _file, cb) => cb(null, LETTERHEADS_DIR),
  filename: (_req, file, cb) => {
    const ext = path.extname(file.originalname) || ".bin";
    cb(null, `${Date.now()}-lh${ext}`);
  },
});
const uploadLetterhead = multer({
  storage: letterheadStorage,
  limits: { fileSize: 10 * 1024 * 1024 },
  fileFilter: (_req, file, cb) => {
    const allowed = ["application/vnd.openxmlformats-officedocument.wordprocessingml.document", "image/png", "image/jpeg", "image/jpg"];
    if (allowed.includes(file.mimetype)) return cb(null, true);
    cb(new Error("Only .docx and image files are allowed"));
  },
});

const templateStorage = multer.diskStorage({
  destination: (_req, _file, cb) => cb(null, TEMPLATES_DIR),
  filename: (_req, file, cb) => {
    const ext = path.extname(file.originalname) || ".docx";
    cb(null, `${Date.now()}-tpl${ext}`);
  },
});
const uploadTemplate = multer({
  storage: templateStorage,
  limits: { fileSize: 20 * 1024 * 1024 },
  fileFilter: (_req, file, cb) => {
    if (file.mimetype === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") return cb(null, true);
    cb(new Error("Only .docx files are allowed for templates"));
  },
});

async function runMigrations() {
  try {
    await db.execute(sql`ALTER TABLE proposal_drafts ADD COLUMN IF NOT EXISTS financial_summary TEXT`);
    await db.execute(sql`ALTER TABLE proposal_drafts ADD COLUMN IF NOT EXISTS pricing_table TEXT`);
    await db.execute(sql`ALTER TABLE proposal_drafts ADD COLUMN IF NOT EXISTS payment_terms TEXT`);
    await db.execute(sql`ALTER TABLE proposal_drafts ADD COLUMN IF NOT EXISTS financial_guarantees TEXT`);
    await db.execute(sql`ALTER TABLE proposal_drafts ADD COLUMN IF NOT EXISTS total_amount TEXT`);
    await db.execute(sql`ALTER TABLE proposal_drafts ADD COLUMN IF NOT EXISTS generated_images JSONB DEFAULT '{}'`);
    await db.execute(sql`ALTER TABLE proposal_drafts ADD COLUMN IF NOT EXISTS pricing_items JSONB`);
    await db.execute(sql`ALTER TABLE companies ADD COLUMN IF NOT EXISTS letterhead_path TEXT`);
    await db.execute(sql`ALTER TABLE companies ADD COLUMN IF NOT EXISTS letterhead_name TEXT`);
    await db.execute(sql`ALTER TABLE companies ADD COLUMN IF NOT EXISTS doc_template_path TEXT`);
    await db.execute(sql`ALTER TABLE companies ADD COLUMN IF NOT EXISTS doc_template_name TEXT`);
    await db.execute(sql`ALTER TABLE companies ADD COLUMN IF NOT EXISTS template_has_cover BOOLEAN DEFAULT FALSE`);
    // Proposal versions table
    await db.execute(sql`
      CREATE TABLE IF NOT EXISTS proposal_versions (
        id SERIAL PRIMARY KEY,
        tender_id INTEGER NOT NULL,
        version_number INTEGER NOT NULL,
        label TEXT,
        ai_notes TEXT,
        type TEXT NOT NULL DEFAULT 'ai_generated',
        language TEXT NOT NULL DEFAULT 'ar',
        introduction TEXT, scope_understanding TEXT, methodology TEXT,
        team TEXT, timeline TEXT, quality_management TEXT,
        risk_management TEXT, conclusion TEXT,
        financial_summary TEXT, pricing_table TEXT, payment_terms TEXT,
        financial_guarantees TEXT, total_amount TEXT,
        file_path TEXT, file_name TEXT, file_type TEXT,
        created_at TIMESTAMP NOT NULL DEFAULT NOW(),
        created_by TEXT
      )
    `);
  } catch { }
}
runMigrations();

/**
 * Ensure each tender document has a comprehensive cached summary, then return
 * the concatenated summary block ready for use as proposal-generation context.
 * Summaries are computed once and persisted in tender_documents.summary.
 */
async function ensureTenderDocSummary(
  tenderId: number,
  language: "ar" | "en",
): Promise<string> {
  const docs = await db.execute(sql`
    SELECT id, file_name, extracted_text, summary
    FROM tender_documents WHERE tender_id = ${tenderId}
  `);
  const parts: string[] = [];
  for (const row of (docs.rows as any[])) {
    if (!row.extracted_text || row.extracted_text.length < 50) continue;
    let summary: string = row.summary;
    if (!summary || summary.length < 100) {
      try {
        summary = await summarizeTenderDocument(row.extracted_text, language);
        await db.execute(sql`UPDATE tender_documents SET summary = ${summary} WHERE id = ${row.id}`);
      } catch {
        // Fallback: use truncated raw text
        summary = (row.extracted_text as string).slice(0, 4000);
      }
    }
    parts.push(`# ${row.file_name}\n${summary}`);
  }
  return parts.join("\n\n---\n\n");
}

const router = Router();

router.get("/tenders/:id/proposal", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });

    const tender = await db
      .select({ id: tendersTable.id })
      .from(tendersTable)
      .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, companyId)))
      .limit(1);
    if (!tender[0]) return res.status(404).json({ error: "Tender not found" });

    const draft = await db
      .select()
      .from(proposalDraftsTable)
      .where(eq(proposalDraftsTable.tenderId, tenderId))
      .limit(1);
    if (!draft[0]) return res.status(404).json({ error: "No proposal draft found" });
    res.json(draft[0]);
  } catch (err) {
    next(err);
  }
});

router.post("/tenders/:id/proposal/generate", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const { language = "ar", aiNotes } = req.body;
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });

    const [tender, analysis, company, docs] = await Promise.all([
      db.select().from(tendersTable)
        .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, companyId)))
        .limit(1),
      db.select().from(analysesTable)
        .where(eq(analysesTable.tenderId, tenderId))
        .orderBy(desc(analysesTable.createdAt))
        .limit(1),
      db.select().from(companiesTable).where(eq(companiesTable.id, companyId)).limit(1),
      db.select({ extractedText: tenderDocumentsTable.extractedText })
        .from(tenderDocumentsTable)
        .where(eq(tenderDocumentsTable.tenderId, tenderId)),
    ]);

    if (!tender[0]) return res.status(404).json({ error: "Tender not found" });
    if (!analysis[0]) return res.status(400).json({ error: "Please analyze the tender first" });

    const co = company[0];
    const companyProfile = co
      ? `Company: ${co.name}\nSector: ${co.sector || "N/A"}\nExperience: ${co.experience || "N/A"}\nDescription: ${(co as any).description || "N/A"}`
      : "";

    // Use cached comprehensive summary of ALL tender documents (no 8K slice).
    void docs; // kept for backward compat; full text now goes via summary helper
    const docSummary = await ensureTenderDocSummary(tenderId, language as "ar" | "en");

    // ── Create or find draft row first so we can do incremental per-section saves ──
    const existing = await db
      .select()
      .from(proposalDraftsTable)
      .where(eq(proposalDraftsTable.tenderId, tenderId))
      .limit(1);

    let draftId: number;
    if (existing[0]) {
      draftId = existing[0].id;
      await db.update(proposalDraftsTable)
        .set({ language, updatedAt: new Date() })
        .where(eq(proposalDraftsTable.id, draftId));
    } else {
      const inserted = await db.insert(proposalDraftsTable)
        .values({ tenderId, language })
        .returning({ id: proposalDraftsTable.id });
      draftId = inserted[0].id;
    }

    // ── Generate all 13 sections in parallel, broadcasting progress per section ──
    const tenderText = tender[0].description || tender[0].title;
    const currentDraft: Record<string, string> = {};
    let completedCount = 0;
    const failedSections: string[] = [];
    const total = PROPOSAL_SECTION_KEYS.length;

    await Promise.allSettled(
      PROPOSAL_SECTION_KEYS.map((sectionKey) =>
        generateProposalSection({
          sectionKey,
          language: language as "ar" | "en",
          tenderText,
          tenderSummary: docSummary,
          analysis: analysis[0] as any,
          companyProfile,
          currentDraft,
          instructions: aiNotes || undefined,
        }).then(async (content) => {
          currentDraft[sectionKey] = content;
          completedCount++;
          await db.update(proposalDraftsTable)
            .set({ [sectionKey]: content, updatedAt: new Date() } as any)
            .where(eq(proposalDraftsTable.id, draftId));
          broadcastTenderUpdate(tenderId, "proposal:generate-progress", {
            section: sectionKey, status: "done", completed: completedCount, total,
          });
        }).catch((err) => {
          console.error(`generate: section ${sectionKey} failed`, err);
          failedSections.push(sectionKey);
          broadcastTenderUpdate(tenderId, "proposal:generate-progress", {
            section: sectionKey, status: "failed", completed: completedCount, total,
          });
        }),
      ),
    );

    const [finalDraft] = await db
      .select()
      .from(proposalDraftsTable)
      .where(eq(proposalDraftsTable.id, draftId))
      .limit(1);

    broadcastTenderUpdate(tenderId, "proposal:generate-complete", {
      failedSections,
      succeededCount: total - failedSections.length,
      total,
    });
    broadcastTenderUpdate(tenderId, "proposal:update", { action: "generated", draft: finalDraft });
    logAudit({
      userId: req.dbUser!.id,
      userEmail: req.dbUser!.email,
      companyId,
      tenderId,
      action: "generate",
      entityType: "proposal_draft",
      entityId: draftId,
    });
    const responseBody: Record<string, unknown> = { ...finalDraft };
    if (failedSections.length > 0) responseBody.failedSections = failedSections;
    res.json(responseBody);
  } catch (err) {
    next(err);
  }
});

router.patch("/proposals/:draftId", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });

    const existing = await db
      .select({ id: proposalDraftsTable.id, tenderId: proposalDraftsTable.tenderId })
      .from(proposalDraftsTable)
      .innerJoin(tendersTable, eq(proposalDraftsTable.tenderId, tendersTable.id))
      .where(
        and(
          eq(proposalDraftsTable.id, Number(req.params.draftId)),
          eq(tendersTable.companyId, companyId),
        ),
      )
      .limit(1);
    if (!existing[0]) return res.status(404).json({ error: "Not found" });

    const updated = await db
      .update(proposalDraftsTable)
      .set({ ...req.body, updatedAt: new Date() })
      .where(eq(proposalDraftsTable.id, Number(req.params.draftId)))
      .returning();
    if (!updated[0]) return res.status(404).json({ error: "Not found" });
    broadcastTenderUpdate(existing[0].tenderId, "proposal:update", { action: "updated", draft: updated[0] });
    logAudit({
      userId: req.dbUser!.id,
      userEmail: req.dbUser!.email,
      companyId,
      tenderId: existing[0].tenderId,
      action: "update",
      entityType: "proposal_draft",
      entityId: updated[0].id,
    });
    res.json(updated[0]);
  } catch (err) {
    next(err);
  }
});

router.get("/tenders/:id/proposal/export", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const companyId = req.companyId;
    const format = (req.query.format as string) || "docx";
    const type = ((req.query.type as string) || "technical") as "technical" | "financial";
    const language = ((req.query.language as string) || "ar") as "ar" | "en";

    if (!companyId) return res.status(400).json({ error: "No company profile" });

    const [tenderRows, draftRows, companyRows] = await Promise.all([
      db.select({ id: tendersTable.id, title: tendersTable.title })
        .from(tendersTable)
        .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, companyId)))
        .limit(1),
      db.select().from(proposalDraftsTable).where(eq(proposalDraftsTable.tenderId, tenderId)).limit(1),
      db.select().from(companiesTable).where(eq(companiesTable.id, companyId)).limit(1),
    ]);

    if (!tenderRows[0]) return res.status(404).json({ error: "Tender not found" });

    let draftRow = draftRows[0];

    // Auto-generate draft if none exists
    if (!draftRow) {
      const [analysisRows, companyRows2, docRows] = await Promise.all([
        db.select().from(analysesTable)
          .where(eq(analysesTable.tenderId, tenderId))
          .orderBy(desc(analysesTable.createdAt))
          .limit(1),
        db.select().from(companiesTable).where(eq(companiesTable.id, companyId)).limit(1),
        db.select({ extractedText: tenderDocumentsTable.extractedText })
          .from(tenderDocumentsTable)
          .where(eq(tenderDocumentsTable.tenderId, tenderId)),
      ]);
      if (!analysisRows[0]) return res.status(400).json({ error: "Please analyze the tender first before exporting" });

      const co = companyRows2[0];
      const companyProfile = co
        ? `Company: ${co.name}\nSector: ${co.sector || "N/A"}\nExperience: ${co.experience || "N/A"}\nDescription: ${(co as any).description || "N/A"}`
        : "";

      void docRows;
      const docSummary = await ensureTenderDocSummary(tenderId, language);

      const tenderDescription = (tenderRows[0] as any).description || tenderRows[0].title;

      const result = await generateProposal(
        tenderDescription,
        analysisRows[0] as any,
        companyProfile,
        language,
        docSummary || undefined,
      );

      const inserted = await db
        .insert(proposalDraftsTable)
        .values({ tenderId, language, ...result })
        .returning();
      draftRow = inserted[0];
    }

    const proposal = draftRow as Record<string, any>;
    const tender = tenderRows[0];
    const company = companyRows[0];
    const lang: "ar" | "en" = proposal.language === "en" ? "en" : (language as "ar" | "en");

    let letterheadImageBuffer: Buffer | null = null;
    const letterheadPath = (company as any)?.letterheadPath as string | undefined;
    if (letterheadPath && fs.existsSync(letterheadPath)) {
      const ext = path.extname(letterheadPath).toLowerCase();
      if (ext === ".png" || ext === ".jpg" || ext === ".jpeg") {
        letterheadImageBuffer = fs.readFileSync(letterheadPath);
      } else if (ext === ".docx") {
        try {
          const mammoth = await import("mammoth");
          const images: Buffer[] = [];
          await mammoth.default.convertToHtml(
            { buffer: fs.readFileSync(letterheadPath) },
            {
              convertImage: mammoth.default.images.inline(async (image: any) => {
                try {
                  const buf = await image.read();
                  images.push(buf);
                } catch { }
                return { src: "" };
              }),
            },
          );
          if (images.length > 0) letterheadImageBuffer = images[0];
        } catch { }
      }
    }

    const docTitle = type === "technical"
      ? (lang === "ar" ? "العرض_الفني" : "Technical_Proposal")
      : (lang === "ar" ? "العرض_المالي" : "Financial_Proposal");
    const safeTitle = tender.title.replace(/[^\w\u0600-\u06FF]/g, "_").slice(0, 40);
    const filename = `${docTitle}_${safeTitle}.${format}`;

    const withCover = req.query.cover === "true" || !!(company as any)?.templateHasCover;
    const sectionNumbering = req.query.numbering !== "false";
    const forceExport = req.query.force === "true";

    // ── Pre-export gate: refuse to export when any section is empty,
    //    unless caller explicitly passes ?force=true (UI never offers
    //    "Export anyway" for empties — only for "weak" sections).
    const readinessReport = evaluateReadiness(proposal as any);
    const relevant = type === "technical" ? readinessReport.technical : readinessReport.financial;
    const emptyHere = relevant.filter((s) => s.status === "empty");
    if (emptyHere.length > 0 && !forceExport) {
      return res.status(422).json({
        error: lang === "ar"
          ? "لا يمكن التصدير قبل تعبئة الأقسام الفارغة"
          : "Cannot export until empty sections are filled",
        emptySections: emptyHere.map((s) => ({
          key: s.key, arLabel: s.arLabel, enLabel: s.enLabel,
          arMessage: s.arMessage, enMessage: s.enMessage,
        })),
        type,
      });
    }

    const rawImages = (proposal as any)?.generatedImages;
    const imagesMap: Record<string, string> = (rawImages && typeof rawImages === "object") ? rawImages : {};
    const sectionImages: Record<string, Buffer> = {};
    for (const [section, imgPath] of Object.entries(imagesMap)) {
      const p = imgPath as string;
      if (p && fs.existsSync(p)) {
        try { sectionImages[section] = fs.readFileSync(p); } catch { }
      }
    }

    const exportOpts = { sectionNumbering, withCoverPage: withCover, sectionImages };
    const templatePath = (company as any)?.docTemplatePath as string | undefined;
    const hasTemplate = !!(templatePath && fs.existsSync(templatePath));

    if (format === "docx") {
      let buffer: Buffer;
      if (hasTemplate) {
        const templateBuffer = fs.readFileSync(templatePath!);
        const data = buildTemplateData(proposal, tender.title, company?.name || "", lang, sectionNumbering);
        buffer = await generateWordFromTemplate(templateBuffer, data);
      } else {
        buffer = await generateWordDocument(proposal, tender.title, company?.name || "", type, lang, letterheadImageBuffer, exportOpts);
      }
      res.setHeader("Content-Type", "application/vnd.openxmlformats-officedocument.wordprocessingml.document");
      res.setHeader("Content-Disposition", `attachment; filename*=UTF-8''${encodeURIComponent(filename)}`);
      res.send(buffer);
    } else if (format === "pdf") {
      const buffer = await generatePdfDocument(proposal, tender.title, company?.name || "", type, lang, letterheadImageBuffer, exportOpts);
      res.setHeader("Content-Type", "application/pdf");
      res.setHeader("Content-Disposition", `attachment; filename*=UTF-8''${encodeURIComponent(filename)}`);
      res.send(buffer);
    } else {
      res.status(400).json({ error: "Invalid format. Use docx or pdf" });
    }
  } catch (err) {
    next(err);
  }
});

router.post("/tenders/:id/proposal/generate-image", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });

    const { section, prompt, model = "dall-e-3" } = req.body;
    if (!section || !prompt) return res.status(400).json({ error: "section and prompt are required" });

    const tenderRows = await db.select({ id: tendersTable.id })
      .from(tendersTable)
      .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, companyId)))
      .limit(1);
    if (!tenderRows[0]) return res.status(404).json({ error: "Tender not found" });

    const draftRows = await db.select({ id: proposalDraftsTable.id })
      .from(proposalDraftsTable)
      .where(eq(proposalDraftsTable.tenderId, tenderId))
      .limit(1);
    if (!draftRows[0]) return res.status(404).json({ error: "No proposal draft found" });

    const b64 = await generateProposalImage(prompt, model as "dall-e-3" | "gpt-image-1");
    const imgBuf = Buffer.from(b64, "base64");
    const filename = `${Date.now()}-${section}.png`;
    const imgPath = path.join(IMAGES_DIR, filename);
    fs.writeFileSync(imgPath, imgBuf);

    const existingImagesRows = await db.execute(
      sql`SELECT generated_images FROM proposal_drafts WHERE id = ${draftRows[0].id}`,
    );
    const existingImages = (existingImagesRows.rows?.[0] as any)?.generated_images ?? {};
    const oldPath = existingImages[section];
    if (oldPath && fs.existsSync(oldPath)) {
      try { fs.unlinkSync(oldPath); } catch { }
    }
    const newImages = { ...existingImages, [section]: imgPath };
    await db.execute(
      sql`UPDATE proposal_drafts SET generated_images = ${JSON.stringify(newImages)}::jsonb WHERE id = ${draftRows[0].id}`,
    );

    res.json({ imageUrl: `/api/tenders/${tenderId}/proposal/images/${section}`, section });
  } catch (err) {
    next(err);
  }
});

router.get("/tenders/:id/proposal/images/:section", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const companyId = req.companyId;
    const section = req.params.section;
    if (!companyId) return res.status(400).json({ error: "No company profile" });

    const tenderRows = await db.select({ id: tendersTable.id })
      .from(tendersTable)
      .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, companyId)))
      .limit(1);
    if (!tenderRows[0]) return res.status(404).json({ error: "Tender not found" });

    const draftRows = await db.execute(
      sql`SELECT generated_images FROM proposal_drafts WHERE tender_id = ${tenderId}`,
    );
    const images = (draftRows.rows?.[0] as any)?.generated_images ?? {};
    const imgPath = images[section];
    if (!imgPath || !fs.existsSync(imgPath)) return res.status(404).json({ error: "Image not found" });

    res.setHeader("Content-Type", "image/png");
    res.setHeader("Cache-Control", "private, max-age=86400");
    res.send(fs.readFileSync(imgPath));
  } catch (err) {
    next(err);
  }
});

router.patch("/company/template-cover", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });
    const hasCover = !!req.body.hasCover;
    await db.execute(
      sql`UPDATE companies SET template_has_cover = ${hasCover} WHERE id = ${companyId}`,
    );
    res.json({ success: true, hasCover });
  } catch (err) {
    next(err);
  }
});

router.post("/company/letterhead", requireAuth, uploadLetterhead.single("file"), async (req: AuthRequest, res, next) => {
  try {
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });
    if (!req.file) return res.status(400).json({ error: "No file uploaded" });

    const filePath = req.file.path;
    let originalName = req.file.originalname;
    try {
      originalName = Buffer.from(originalName, "latin1").toString("utf8");
    } catch { }

    await db.execute(
      sql`UPDATE companies SET letterhead_path = ${filePath}, letterhead_name = ${originalName} WHERE id = ${companyId}`,
    );

    res.json({ success: true, name: originalName });
  } catch (err) {
    next(err);
  }
});

router.delete("/company/letterhead", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });

    const rows = await db.execute(
      sql`SELECT letterhead_path FROM companies WHERE id = ${companyId}`,
    );
    const lp = (rows.rows?.[0] as any)?.letterhead_path;
    if (lp && fs.existsSync(lp)) {
      try { fs.unlinkSync(lp); } catch { }
    }

    await db.execute(
      sql`UPDATE companies SET letterhead_path = NULL, letterhead_name = NULL WHERE id = ${companyId}`,
    );

    res.json({ success: true });
  } catch (err) {
    next(err);
  }
});

router.get("/company/letterhead", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });

    const rows = await db.execute(
      sql`SELECT letterhead_path, letterhead_name FROM companies WHERE id = ${companyId}`,
    );
    const row = rows.rows?.[0] as any;
    res.json({ path: row?.letterhead_path || null, name: row?.letterhead_name || null });
  } catch (err) {
    next(err);
  }
});

router.post("/company/template", requireAuth, uploadTemplate.single("file"), async (req: AuthRequest, res, next) => {
  try {
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });
    if (!req.file) return res.status(400).json({ error: "No file uploaded" });

    const oldRows = await db.execute(
      sql`SELECT doc_template_path FROM companies WHERE id = ${companyId}`,
    );
    const oldPath = (oldRows.rows?.[0] as any)?.doc_template_path;
    if (oldPath && fs.existsSync(oldPath)) {
      try { fs.unlinkSync(oldPath); } catch { }
    }

    const filePath = req.file.path;
    let originalName = req.file.originalname;
    try { originalName = Buffer.from(originalName, "latin1").toString("utf8"); } catch { }

    await db.execute(
      sql`UPDATE companies SET doc_template_path = ${filePath}, doc_template_name = ${originalName} WHERE id = ${companyId}`,
    );
    res.json({ success: true, name: originalName });
  } catch (err) {
    next(err);
  }
});

router.get("/company/template", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });

    const rows = await db.execute(
      sql`SELECT doc_template_path, doc_template_name, template_has_cover FROM companies WHERE id = ${companyId}`,
    );
    const row = rows.rows?.[0] as any;
    res.json({
      path: row?.doc_template_path || null,
      name: row?.doc_template_name || null,
      hasCover: row?.template_has_cover ?? false,
    });
  } catch (err) {
    next(err);
  }
});

router.delete("/company/template", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });

    const rows = await db.execute(
      sql`SELECT doc_template_path FROM companies WHERE id = ${companyId}`,
    );
    const tp = (rows.rows?.[0] as any)?.doc_template_path;
    if (tp && fs.existsSync(tp)) {
      try { fs.unlinkSync(tp); } catch { }
    }

    await db.execute(
      sql`UPDATE companies SET doc_template_path = NULL, doc_template_name = NULL WHERE id = ${companyId}`,
    );
    res.json({ success: true });
  } catch (err) {
    next(err);
  }
});

// ─── Pre-export readiness ──────────────────────────────────────────────────

router.get("/tenders/:id/proposal/readiness", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });

    const tender = await db.select({ id: tendersTable.id }).from(tendersTable)
      .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, companyId))).limit(1);
    if (!tender[0]) return res.status(404).json({ error: "Tender not found" });

    const draftRows = await db.select().from(proposalDraftsTable)
      .where(eq(proposalDraftsTable.tenderId, tenderId)).limit(1);
    if (!draftRows[0]) return res.status(404).json({ error: "No proposal draft found" });

    res.json(evaluateReadiness(draftRows[0] as any));
  } catch (err) { next(err); }
});

// ─── Market-based pricing context for financial regeneration ──────────────

router.get("/tenders/:id/proposal/pricing-context", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const companyId = req.companyId;
    const language = ((req.query.language as string) || "ar") as "ar" | "en";
    if (!companyId) return res.status(400).json({ error: "No company profile" });

    const tender = await db.select().from(tendersTable)
      .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, companyId))).limit(1);
    if (!tender[0]) return res.status(404).json({ error: "Tender not found" });

    // Gather prior proposals from the SAME COMPANY (across tenders, excluding this one).
    const prior = await db.execute(sql`
      SELECT t.title, pd.pricing_table, pd.total_amount
      FROM proposal_drafts pd
      JOIN tenders t ON t.id = pd.tender_id
      WHERE t.company_id = ${companyId} AND pd.tender_id <> ${tenderId}
      ORDER BY pd.updated_at DESC NULLS LAST
      LIMIT 10
    `);

    const summary = await ensureTenderDocSummary(tenderId, language).catch(() => "");

    // Web-based market search (Tavily) — graceful fallback when not configured.
    const { searchMarketPrices, formatMarketSearchContext } = await import("../lib/marketSearch.js");
    const webResults = await searchMarketPrices({
      tenderTitle: tender[0].title, language,
      category: (tender[0] as any).category ?? null,
    });
    const webContext = formatMarketSearchContext(webResults, language);
    const webConfigured = webResults[0]?.configured ?? false;
    const webWarning = !webConfigured ? webResults[0]?.warning : undefined;
    const webSources = webResults.flatMap((r) => r.sources).slice(0, 12);

    const context = await inferMarketPricing({
      language,
      tenderTitle: tender[0].title,
      tenderSummary: summary,
      pastProposals: (prior.rows as any[]).map((r) => ({
        title: r.title, pricingTable: r.pricing_table, totalAmount: r.total_amount,
      })),
      webContext: webContext || undefined,
    });

    res.json({
      context,
      pastProposalCount: (prior.rows as any[]).filter((r) => r.total_amount || r.pricing_table).length,
      webSearch: {
        configured: webConfigured,
        warning: webWarning,
        sources: webSources,
        sourceCount: webSources.length,
      },
    });
  } catch (err) { next(err); }
});

// ─── Per-section regeneration ─────────────────────────────────────────────

router.post("/tenders/:id/proposal/section/:key/regenerate", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const sectionKey = req.params.key;
    const companyId = req.companyId;
    const { instructions, language: bodyLang, useMarketPricing } = req.body || {};
    if (!companyId) return res.status(400).json({ error: "No company profile" });
    if (!PROPOSAL_SECTION_KEYS.includes(sectionKey)) {
      return res.status(400).json({ error: `Unknown section: ${sectionKey}` });
    }

    const [tender, analysis, company, draftRows] = await Promise.all([
      db.select().from(tendersTable)
        .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, companyId))).limit(1),
      db.select().from(analysesTable)
        .where(eq(analysesTable.tenderId, tenderId))
        .orderBy(desc(analysesTable.createdAt)).limit(1),
      db.select().from(companiesTable).where(eq(companiesTable.id, companyId)).limit(1),
      db.select().from(proposalDraftsTable).where(eq(proposalDraftsTable.tenderId, tenderId)).limit(1),
    ]);
    if (!tender[0]) return res.status(404).json({ error: "Tender not found" });
    if (!analysis[0]) return res.status(400).json({ error: "Please analyze the tender first" });
    if (!draftRows[0]) return res.status(404).json({ error: "No proposal draft found — generate one first" });

    const draft = draftRows[0] as any;
    const lang: "ar" | "en" = (bodyLang || draft.language || "ar") as "ar" | "en";
    const co = company[0];
    const companyProfile = co
      ? `Company: ${co.name}\nSector: ${co.sector || "N/A"}\nExperience: ${co.experience || "N/A"}\nDescription: ${(co as any).description || "N/A"}`
      : "";

    // Camel-case mirror for cross-section consistency context
    const currentDraft: Record<string, string> = {};
    for (const k of PROPOSAL_SECTION_KEYS) {
      const snake = k.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`);
      currentDraft[k] = draft[k] ?? draft[snake] ?? "";
    }

    const docSummary = await ensureTenderDocSummary(tenderId, lang);

    let pricingContext: string | undefined;
    const isFinancial = ["financialSummary", "pricingTable", "paymentTerms", "financialGuarantees", "totalAmount"].includes(sectionKey);
    if (isFinancial && useMarketPricing !== false) {
      try {
        const prior = await db.execute(sql`
          SELECT t.title, pd.pricing_table, pd.total_amount
          FROM proposal_drafts pd JOIN tenders t ON t.id = pd.tender_id
          WHERE t.company_id = ${companyId} AND pd.tender_id <> ${tenderId}
          ORDER BY pd.updated_at DESC NULLS LAST LIMIT 8
        `);
        // Web search (Tavily) — silently degrades when not configured.
        let webContext = "";
        try {
          const { searchMarketPrices, formatMarketSearchContext } = await import("../lib/marketSearch.js");
          const webResults = await searchMarketPrices({
            tenderTitle: tender[0].title, language: lang,
            category: (tender[0] as any).category ?? null,
          });
          webContext = formatMarketSearchContext(webResults, lang);
        } catch { /* ignore */ }
        pricingContext = await inferMarketPricing({
          language: lang, tenderTitle: tender[0].title, tenderSummary: docSummary,
          pastProposals: (prior.rows as any[]).map((r) => ({
            title: r.title, pricingTable: r.pricing_table, totalAmount: r.total_amount,
          })),
          webContext: webContext || undefined,
        });
      } catch { /* graceful: continue without market context */ }
    }

    const newContent = await generateProposalSection({
      sectionKey, language: lang, tenderText: tender[0].description || tender[0].title,
      tenderSummary: docSummary, analysis: analysis[0] as any, companyProfile,
      currentDraft, instructions, pricingContext,
    });

    const colName = sectionKey.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`);
    const setMap: Record<string, any> = {};
    setMap[sectionKey] = newContent;

    // If we just regenerated the pricing table, parse it into structured
    // line items, recompute subtotal/VAT/total deterministically server-side,
    // store the structured doc in `pricing_items`, replace the markdown with
    // a canonical re-rendering, and refresh `total_amount`.
    if (sectionKey === "pricingTable") {
      try {
        const { parseMarkdownPricingTable, renderPricingTableMarkdown, formatTotalAmount } = await import("../lib/pricing.js");
        const doc = parseMarkdownPricingTable(newContent);
        if (doc && doc.items.length > 0) {
          setMap.pricingTable = renderPricingTableMarkdown(doc, lang);
          (setMap as any).pricingItems = doc;
          (setMap as any).totalAmount = formatTotalAmount(doc, lang);
        }
      } catch { /* fall back to raw AI markdown if parsing fails */ }
    }

    await db.update(proposalDraftsTable)
      .set({ ...setMap, updatedAt: new Date() })
      .where(eq(proposalDraftsTable.id, draft.id));

    // Auto-save a version snapshot (lightweight, only if there was a previous draft)
    try {
      const lastVer = await db.execute(
        sql`SELECT COALESCE(MAX(version_number), 0) AS max_ver FROM proposal_versions WHERE tender_id = ${tenderId}`,
      );
      const nextVer = ((lastVer.rows[0] as any)?.max_ver ?? 0) + 1;
      const label = lang === "ar" ? `قبل إعادة توليد: ${sectionKey}` : `Before regen: ${sectionKey}`;
      // `draft` comes from Drizzle (camelCase); `currentDraft` is the camelCase
      // mirror we already built above — use it so every section is reliably
      // persisted whether the source was camel- or snake-cased.
      await db.execute(sql`
        INSERT INTO proposal_versions
          (tender_id, version_number, label, ai_notes, type, language,
           introduction, scope_understanding, methodology, team, timeline,
           quality_management, risk_management, conclusion,
           financial_summary, pricing_table, payment_terms,
           financial_guarantees, total_amount, created_by)
        VALUES
          (${tenderId}, ${nextVer}, ${label}, ${instructions || null}, 'ai_generated', ${lang},
           ${currentDraft.introduction || null}, ${currentDraft.scopeUnderstanding || null},
           ${currentDraft.methodology || null}, ${currentDraft.team || null}, ${currentDraft.timeline || null},
           ${currentDraft.qualityManagement || null}, ${currentDraft.riskManagement || null},
           ${currentDraft.conclusion || null},
           ${currentDraft.financialSummary || null}, ${currentDraft.pricingTable || null},
           ${currentDraft.paymentTerms || null}, ${currentDraft.financialGuarantees || null},
           ${currentDraft.totalAmount || null}, ${req.dbUser?.email || null})
      `);
      void colName;
    } catch { /* version snapshot is best-effort */ }

    const fresh = await db.select().from(proposalDraftsTable)
      .where(eq(proposalDraftsTable.id, draft.id)).limit(1);
    broadcastTenderUpdate(tenderId, "proposal:update", { action: "section_regenerated", section: sectionKey });
    res.json({ section: sectionKey, content: newContent, draft: fresh[0] });
  } catch (err) { next(err); }
});

// ─── Fill all financial sections from market pricing ──────────────────────

router.post("/tenders/:id/proposal/fill-financial", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const companyId = req.companyId;
    const language = ((req.body?.language as string) || "ar") as "ar" | "en";
    if (!companyId) return res.status(400).json({ error: "No company profile" });

    const [tender, analysis, company, draftRows] = await Promise.all([
      db.select().from(tendersTable)
        .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, companyId))).limit(1),
      db.select().from(analysesTable)
        .where(eq(analysesTable.tenderId, tenderId))
        .orderBy(desc(analysesTable.createdAt)).limit(1),
      db.select().from(companiesTable).where(eq(companiesTable.id, companyId)).limit(1),
      db.select().from(proposalDraftsTable).where(eq(proposalDraftsTable.tenderId, tenderId)).limit(1),
    ]);

    if (!tender[0]) return res.status(404).json({ error: "Tender not found" });
    if (!analysis[0]) return res.status(400).json({ error: "Please analyze the tender first" });
    if (!draftRows[0]) return res.status(404).json({ error: "No proposal draft found — generate one first" });

    const draft = draftRows[0] as any;
    const co = company[0];
    const companyProfile = co
      ? `Company: ${co.name}\nSector: ${co.sector || "N/A"}\nExperience: ${co.experience || "N/A"}\nDescription: ${(co as any).description || "N/A"}`
      : "";

    const currentDraft: Record<string, string> = {};
    for (const k of PROPOSAL_SECTION_KEYS) {
      const snake = k.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`);
      currentDraft[k] = draft[k] ?? draft[snake] ?? "";
    }

    const docSummary = await ensureTenderDocSummary(tenderId, language).catch(() => "");

    // Gather market pricing context
    const prior = await db.execute(sql`
      SELECT t.title, pd.pricing_table, pd.total_amount
      FROM proposal_drafts pd JOIN tenders t ON t.id = pd.tender_id
      WHERE t.company_id = ${companyId} AND pd.tender_id <> ${tenderId}
      ORDER BY pd.updated_at DESC NULLS LAST LIMIT 8
    `);

    let webContext = "";
    try {
      const { searchMarketPrices, formatMarketSearchContext } = await import("../lib/marketSearch.js");
      const webResults = await searchMarketPrices({
        tenderTitle: tender[0].title, language,
        category: (tender[0] as any).category ?? null,
      });
      webContext = formatMarketSearchContext(webResults, language);
    } catch { /* graceful */ }

    const pricingContext = await inferMarketPricing({
      language, tenderTitle: tender[0].title, tenderSummary: docSummary,
      pastProposals: (prior.rows as any[]).map((r) => ({
        title: r.title, pricingTable: r.pricing_table, totalAmount: r.total_amount,
      })),
      webContext: webContext || undefined,
    });

    // Save a version snapshot of all 13 sections before overwriting the financial ones
    try {
      const lastVer = await db.execute(
        sql`SELECT COALESCE(MAX(version_number), 0) AS max_ver FROM proposal_versions WHERE tender_id = ${tenderId}`,
      );
      const nextVer = ((lastVer.rows[0] as any)?.max_ver ?? 0) + 1;
      const label = language === "ar" ? "قبل الملء المالي التلقائي" : "Before auto-fill financial";
      await db.execute(sql`
        INSERT INTO proposal_versions
          (tender_id, version_number, label, ai_notes, type, language,
           introduction, scope_understanding, methodology, team, timeline,
           quality_management, risk_management, conclusion,
           financial_summary, pricing_table, payment_terms,
           financial_guarantees, total_amount, created_by)
        VALUES
          (${tenderId}, ${nextVer}, ${label}, ${null}, 'ai_generated', ${language},
           ${currentDraft.introduction || null}, ${currentDraft.scopeUnderstanding || null},
           ${currentDraft.methodology || null}, ${currentDraft.team || null}, ${currentDraft.timeline || null},
           ${currentDraft.qualityManagement || null}, ${currentDraft.riskManagement || null},
           ${currentDraft.conclusion || null},
           ${currentDraft.financialSummary || null}, ${currentDraft.pricingTable || null},
           ${currentDraft.paymentTerms || null}, ${currentDraft.financialGuarantees || null},
           ${currentDraft.totalAmount || null}, ${req.dbUser?.email || null})
      `);
    } catch { /* version snapshot is best-effort */ }

    // ── Generate all 5 financial sections in parallel ─────────────────────
    // Each section saves to DB and broadcasts progress as soon as it finishes,
    // giving the client a real-time step-by-step checklist.
    const FINANCIAL_KEYS = ["financialSummary", "pricingTable", "paymentTerms", "financialGuarantees", "totalAmount"];
    let completedCount = 0;
    const sections: Record<string, string> = {};

    await Promise.allSettled(
      FINANCIAL_KEYS.map((sectionKey) =>
        generateProposalSection({
          sectionKey, language,
          tenderText: tender[0].description || tender[0].title,
          tenderSummary: docSummary, analysis: analysis[0] as any,
          companyProfile, currentDraft, pricingContext,
        }).then(async (content) => {
          sections[sectionKey] = content;
          completedCount++;
          // Save this section to DB immediately
          await db.update(proposalDraftsTable)
            .set({ [sectionKey]: content, updatedAt: new Date() } as any)
            .where(eq(proposalDraftsTable.id, draft.id));
          // Broadcast per-section progress to the client
          broadcastTenderUpdate(tenderId, "proposal:financial-progress", {
            section: sectionKey, completed: completedCount, total: 5,
          });
        }).catch((err) => {
          // Log failure but don't abort the other sections
          console.error(`fill-financial: section ${sectionKey} failed`, err);
        }),
      ),
    );

    // Post-process: parse pricingTable → structured items + canonical total
    if (sections.pricingTable) {
      try {
        const { parseMarkdownPricingTable, renderPricingTableMarkdown, formatTotalAmount } = await import("../lib/pricing.js");
        const doc = parseMarkdownPricingTable(sections.pricingTable);
        if (doc && doc.items.length > 0) {
          sections.pricingTable = renderPricingTableMarkdown(doc, language);
          sections.totalAmount = formatTotalAmount(doc, language);
          await db.update(proposalDraftsTable)
            .set({
              pricingTable: sections.pricingTable,
              pricingItems: doc,
              totalAmount: sections.totalAmount,
              updatedAt: new Date(),
            } as any)
            .where(eq(proposalDraftsTable.id, draft.id));
        }
      } catch { /* fall back to raw markdown */ }
    }

    broadcastTenderUpdate(tenderId, "proposal:update", { action: "financial_sections_filled" });

    res.json({ sections });
  } catch (err) { next(err); }
});

// ─── Proposal Versions ─────────────────────────────────────────────────────

const VERSIONS_DIR = path.join(UPLOADS_DIR, "proposal-versions");
fs.mkdirSync(VERSIONS_DIR, { recursive: true });

const versionFileStorage = multer.diskStorage({
  destination: (_req, _file, cb) => cb(null, VERSIONS_DIR),
  filename: (_req, file, cb) => {
    const ext = path.extname(file.originalname) || ".bin";
    cb(null, `${Date.now()}-v${ext}`);
  },
});
const uploadVersionFile = multer({
  storage: versionFileStorage,
  limits: { fileSize: 30 * 1024 * 1024 },
  fileFilter: (_req, file, cb) => {
    const allowed = [
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/pdf",
    ];
    if (allowed.includes(file.mimetype)) return cb(null, true);
    cb(new Error("Only .docx and .pdf files are allowed"));
  },
});

// GET all versions for a tender
router.get("/tenders/:id/proposal/versions", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });

    const tender = await db.select({ id: tendersTable.id }).from(tendersTable)
      .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, companyId))).limit(1);
    if (!tender[0]) return res.status(404).json({ error: "Tender not found" });

    const versions = await db.execute(
      sql`SELECT * FROM proposal_versions WHERE tender_id = ${tenderId} ORDER BY version_number DESC`,
    );
    res.json(versions.rows);
  } catch (err) { next(err); }
});

// POST save current draft as a new version
router.post("/tenders/:id/proposal/versions", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const companyId = req.companyId;
    const { label } = req.body;
    if (!companyId) return res.status(400).json({ error: "No company profile" });

    const [tenderRows, draftRows] = await Promise.all([
      db.select({ id: tendersTable.id }).from(tendersTable)
        .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, companyId))).limit(1),
      db.execute(sql`SELECT * FROM proposal_drafts WHERE tender_id = ${tenderId} LIMIT 1`),
    ]);
    if (!tenderRows[0]) return res.status(404).json({ error: "Tender not found" });
    if (!draftRows.rows[0]) return res.status(404).json({ error: "No proposal draft found" });

    const draft = draftRows.rows[0] as any;
    const lastVer = await db.execute(
      sql`SELECT COALESCE(MAX(version_number), 0) AS max_ver FROM proposal_versions WHERE tender_id = ${tenderId}`,
    );
    const nextVer = ((lastVer.rows[0] as any)?.max_ver ?? 0) + 1;

    const inserted = await db.execute(sql`
      INSERT INTO proposal_versions
        (tender_id, version_number, label, type, language,
         introduction, scope_understanding, methodology, team, timeline,
         quality_management, risk_management, conclusion,
         financial_summary, pricing_table, payment_terms,
         financial_guarantees, total_amount, created_by)
      VALUES
        (${tenderId}, ${nextVer}, ${label || null}, 'ai_generated', ${draft.language || "ar"},
         ${draft.introduction || null}, ${draft.scope_understanding || null},
         ${draft.methodology || null}, ${draft.team || null}, ${draft.timeline || null},
         ${draft.quality_management || null}, ${draft.risk_management || null},
         ${draft.conclusion || null},
         ${draft.financial_summary || null}, ${draft.pricing_table || null},
         ${draft.payment_terms || null}, ${draft.financial_guarantees || null},
         ${draft.total_amount || null}, ${req.dbUser?.email || null})
      RETURNING *
    `);
    res.json(inserted.rows[0]);
  } catch (err) { next(err); }
});

// POST upload a file as a new version
router.post("/tenders/:id/proposal/versions/upload", requireAuth, uploadVersionFile.single("file"),
  async (req: AuthRequest, res, next) => {
    try {
      const tenderId = Number(req.params.id);
      const companyId = req.companyId;
      const { label } = req.body;
      if (!companyId) return res.status(400).json({ error: "No company profile" });
      if (!req.file) return res.status(400).json({ error: "No file uploaded" });

      const tender = await db.select({ id: tendersTable.id }).from(tendersTable)
        .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, companyId))).limit(1);
      if (!tender[0]) return res.status(404).json({ error: "Tender not found" });

      const lastVer = await db.execute(
        sql`SELECT COALESCE(MAX(version_number), 0) AS max_ver FROM proposal_versions WHERE tender_id = ${tenderId}`,
      );
      const nextVer = ((lastVer.rows[0] as any)?.max_ver ?? 0) + 1;
      const fileType = req.file.mimetype.includes("pdf") ? "pdf" : "docx";

      const inserted = await db.execute(sql`
        INSERT INTO proposal_versions
          (tender_id, version_number, label, type, language, file_path, file_name, file_type, created_by)
        VALUES
          (${tenderId}, ${nextVer}, ${label || null}, 'manual_upload', 'ar',
           ${req.file.path}, ${req.file.originalname}, ${fileType}, ${req.dbUser?.email || null})
        RETURNING *
      `);
      res.json(inserted.rows[0]);
    } catch (err) { next(err); }
  },
);

// DELETE a version
router.delete("/tenders/:id/proposal/versions/:versionId", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const versionId = Number(req.params.versionId);
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });

    const tender = await db.select({ id: tendersTable.id }).from(tendersTable)
      .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, companyId))).limit(1);
    if (!tender[0]) return res.status(404).json({ error: "Tender not found" });

    const ver = await db.execute(
      sql`SELECT file_path FROM proposal_versions WHERE id = ${versionId} AND tender_id = ${tenderId}`,
    );
    if (!ver.rows[0]) return res.status(404).json({ error: "Version not found" });

    const filePath = (ver.rows[0] as any)?.file_path;
    if (filePath && fs.existsSync(filePath)) {
      try { fs.unlinkSync(filePath); } catch { }
    }
    await db.execute(sql`DELETE FROM proposal_versions WHERE id = ${versionId} AND tender_id = ${tenderId}`);
    res.json({ success: true });
  } catch (err) { next(err); }
});

// GET download a version's file
router.get("/tenders/:id/proposal/versions/:versionId/file", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const versionId = Number(req.params.versionId);
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });

    const tender = await db.select({ id: tendersTable.id }).from(tendersTable)
      .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, companyId))).limit(1);
    if (!tender[0]) return res.status(404).json({ error: "Tender not found" });

    const ver = await db.execute(
      sql`SELECT * FROM proposal_versions WHERE id = ${versionId} AND tender_id = ${tenderId}`,
    );
    if (!ver.rows[0]) return res.status(404).json({ error: "Version not found" });

    const v = ver.rows[0] as any;
    if (v.type === "manual_upload" && v.file_path && fs.existsSync(v.file_path)) {
      const mime = v.file_type === "pdf" ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      res.setHeader("Content-Type", mime);
      res.setHeader("Content-Disposition", `attachment; filename*=UTF-8''${encodeURIComponent(v.file_name || `version-${v.version_number}.${v.file_type}`)}`);
      return res.send(fs.readFileSync(v.file_path));
    }

    // For AI-generated versions, export as docx on the fly
    const [tenderRow, companyRow] = await Promise.all([
      db.select({ title: tendersTable.title }).from(tendersTable).where(eq(tendersTable.id, tenderId)).limit(1),
      db.select().from(companiesTable).where(eq(companiesTable.id, companyId)).limit(1),
    ]);
    const format = (req.query.format as string) || "docx";
    const type = ((req.query.type as string) || "technical") as "technical" | "financial";
    const lang = (v.language || "ar") as "ar" | "en";
    const buffer = format === "pdf"
      ? await generatePdfDocument(v, tenderRow[0]?.title || "", (companyRow[0] as any)?.name || "", type, lang, null, {})
      : await generateWordDocument(v, tenderRow[0]?.title || "", (companyRow[0] as any)?.name || "", type, lang, null, {});
    const mime = format === "pdf" ? "application/pdf"
      : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    const filename = `v${v.version_number}-${type}.${format}`;
    res.setHeader("Content-Type", mime);
    res.setHeader("Content-Disposition", `attachment; filename*=UTF-8''${encodeURIComponent(filename)}`);
    res.send(buffer);
  } catch (err) { next(err); }
});

// POST restore a version as the current draft
router.post("/tenders/:id/proposal/versions/:versionId/restore", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const versionId = Number(req.params.versionId);
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });

    const tender = await db.select({ id: tendersTable.id }).from(tendersTable)
      .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, companyId))).limit(1);
    if (!tender[0]) return res.status(404).json({ error: "Tender not found" });

    const ver = await db.execute(
      sql`SELECT * FROM proposal_versions WHERE id = ${versionId} AND tender_id = ${tenderId}`,
    );
    if (!ver.rows[0]) return res.status(404).json({ error: "Version not found" });
    const v = ver.rows[0] as any;
    if (v.type !== "ai_generated") return res.status(400).json({ error: "Only AI-generated versions can be restored" });

    const existing = await db.select({ id: proposalDraftsTable.id }).from(proposalDraftsTable)
      .where(eq(proposalDraftsTable.tenderId, tenderId)).limit(1);

    const fields = {
      language: v.language, introduction: v.introduction, scopeUnderstanding: v.scope_understanding,
      methodology: v.methodology, team: v.team, timeline: v.timeline,
      qualityManagement: v.quality_management, riskManagement: v.risk_management,
      conclusion: v.conclusion, updatedAt: new Date(),
    };
    const financialFields = {
      financialSummary: v.financial_summary, pricingTable: v.pricing_table,
      paymentTerms: v.payment_terms, financialGuarantees: v.financial_guarantees,
      totalAmount: v.total_amount,
    };

    let draft;
    if (existing[0]) {
      await db.execute(sql`
        UPDATE proposal_drafts SET
          language = ${v.language}, introduction = ${v.introduction},
          scope_understanding = ${v.scope_understanding}, methodology = ${v.methodology},
          team = ${v.team}, timeline = ${v.timeline},
          quality_management = ${v.quality_management}, risk_management = ${v.risk_management},
          conclusion = ${v.conclusion}, financial_summary = ${v.financial_summary},
          pricing_table = ${v.pricing_table}, payment_terms = ${v.payment_terms},
          financial_guarantees = ${v.financial_guarantees}, total_amount = ${v.total_amount},
          updated_at = NOW()
        WHERE id = ${existing[0].id}
      `);
      draft = { id: existing[0].id, tenderId, ...fields, ...financialFields };
    } else {
      const ins = await db.insert(proposalDraftsTable)
        .values({ tenderId, ...fields }).returning();
      draft = ins[0];
    }

    broadcastTenderUpdate(tenderId, "proposal:update", { action: "restored", draft });
    res.json({ success: true, draft });
  } catch (err) { next(err); }
});

export default router;
